import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pnidApi, redundancySetApi } from '../api';
import { queryKeys } from '../queries/keys';
import type { RedundancySet, RedundancySetConflict, TagSuggestionKind } from '../types';
import { normalizeAssetTag } from '../utils/normalizeAssetTag';

const TAG_SUGGESTION_LIMIT = 20;

/**
 * Every redundancy set of one plant.
 *
 * DECISION(2026-09-17, abhishek): The frontend reads a plant's sets through React
 * Query and keeps them cached. Opening an equipment fills its redundancy-set
 * section from that cache, with no request per open.
 * An equipment opens knowing its tag, not a set id, and the picker offers every set,
 * so the plant's list is what both need. Every drawing of a plant shares the
 * one entry.
 */
export function useRedundancySets(domainId: string) {
  return useQuery({
    queryKey: queryKeys.redundancySets.list(domainId),
    queryFn: () => redundancySetApi.list(domainId),
    enabled: Boolean(domainId),
  });
}

/** The 409 body of a put whose tags span more than one set, or null for any other failure. */
export function redundancySetConflictOf(error: unknown): RedundancySetConflict | null {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (response?.status !== 409) return null;
  const detail = response.data?.detail as Partial<RedundancySetConflict> | undefined;
  if (detail?.code !== 'redundancy_set_conflict') return null;
  return detail as RedundancySetConflict;
}

export function usePutRedundancySetTags(domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tags: string[]) => redundancySetApi.putTags(domainId, tags),
    onSuccess: (sets) => {
      queryClient.setQueryData(queryKeys.redundancySets.list(domainId), sets);
    },
    onError: (error) => {
      // A conflict means the cached list no longer matches the server's.
      if (redundancySetConflictOf(error)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.redundancySets.list(domainId) });
      }
    },
  });
}

export function useRemoveRedundancySetMember(domainId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tag: string) => redundancySetApi.removeMember(domainId, tag),
    onSuccess: (sets) => {
      queryClient.setQueryData(queryKeys.redundancySets.list(domainId), sets);
    },
  });
}

/** The set that holds `tag`, however the tag's separators are typed. */
export function findRedundancySet(
  sets: RedundancySet[] | undefined,
  tag: string | null | undefined,
): RedundancySet | undefined {
  const normalizedTag = normalizeAssetTag(tag);
  if (!normalizedTag) return undefined;
  return sets?.find((set) => set.tags.includes(normalizedTag));
}

/** Tags of one family that the drawing's plant already uses, for the typed text. */
export function useTagSuggestions(
  docId: string,
  kind: TagSuggestionKind,
  query: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.tagSuggestions(docId, kind, query),
    queryFn: ({ signal }) =>
      pnidApi.getTagSuggestions(docId, { kind, query, limit: TAG_SUGGESTION_LIMIT, signal }),
    enabled: enabled && Boolean(docId),
  });
}
